import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import * as pujaServices from "../../services/puja.services";
import { GetAllAstrologer } from "../../services/psychic.services";
import CustomPagination from "../../common/custompagination";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import CustomRangepicker from "../../common/rangepicker";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select"; // ← New import

const PujaBookingsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [datalength, setDataLength] = useState(0);
  const [itemperpage] = useState(10);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState({});
  const [loader, setLoader] = useState(true);
  const [globalsearch, setGlobalSearch] = useState("");
  const [search, setSearch] = useState({});
  const [tab, setTab] = useState("all");
  const [resetdate, setResetDate] = useState(false);

  // Assign Astrologer Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [astrologers, setAstrologers] = useState([]);
  const [astroSearch, setAstroSearch] = useState("");
  const [astroLoader, setAstroLoader] = useState(false);
  const [selectedAstroOption, setSelectedAstroOption] = useState(null); // For react-select

  const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Puja Bookings", url: "" },
  ];

  const tabs = [
    { key: "all", label: "All" },
    { key: "requested", label: "Requested" },
    { key: "upcoming", label: "Upcoming" },
    { key: "in_progress", label: "In Progress" },
    { key: "past", label: "Past" },
  ];

  // Fetch bookings
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoader(true);
      const formData = new FormData();
      formData.append("page", page);
      formData.append("per_page", itemperpage);
      formData.append("sort", JSON.stringify(sorting));
      formData.append("search", JSON.stringify(search));
      formData.append("global_search", globalsearch);
      formData.append("tab", tab);

      pujaServices
        .BookingList(formData)
        .then((res) => {
          setList(res.data.list || []);
          setDataLength(res.data.total_records || 0);
          setPage(res.data.page || 1);
          setLoader(false);
        })
        .catch((err) => {
          console.log(err);
          setLoader(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [page, sorting, search, globalsearch, tab, resetdate]);

  // Fetch astrologers on modal open or search change
  useEffect(() => {
    if (!showModal) return;

    const timer = setTimeout(() => {
      fetchAstrologers(astroSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [astroSearch, showModal]);

  const fetchAstrologers = (searchTerm = "") => {
    setAstroLoader(true);
    const formData = new FormData();
    if (searchTerm) formData.append("global_search", searchTerm);

    GetAllAstrologer(formData)
      .then((res) => {
        setAstrologers(res.data.list || res.data.astrologers || []);
        setAstroLoader(false);
      })
      .catch((err) => {
        console.log(err);
        setAstroLoader(false);
        Swal.fire("Error", "Failed to load astrologers", "error");
      });
  };

  const handleSort = (column) => {
    let order = sorting.column === column && sorting.order === 1 ? -1 : 1;
    setSorting({ column, order });
  };

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (value && String(value).length > 0) sr[key] = value;
    else delete sr[key];
    setSearch(sr);
  };

  const resetFilter = () => {
    setGlobalSearch("");
    setSearch({});
    setResetDate(!resetdate);
  };

  const openAssignModal = (bookingId) => {
    setSelectedBookingId(bookingId);
    setAstroSearch("");
    setSelectedAstroOption(null);
    fetchAstrologers(); // Load all initially
    setShowModal(true);
  };

  const handleAssign = () => {
    if (!selectedAstroOption) {
      Swal.fire("Warning", "Please select an astrologer", "warning");
      return;
    }

    pujaServices
      .AssignAstrologer({
        puja_id: selectedBookingId,
        astrologer_id: selectedAstroOption.value,
      })
      .then((res) => {
        if (res.success || res.status === 200) {
          Swal.fire("Success", "Astrologer assigned successfully", "success");
          setShowModal(false);
          setResetDate((p) => !p);
        } else {
          throw new Error(res.message || "Assignment failed");
        }
      })
      .catch((err) => {
        Swal.fire("Error", err.message || "Failed to assign", "error");
      });
  };

  const updateStatus = (id, status) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Change status to "${status}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        pujaServices
          .UpdateStatus({ puja_id: id, new_status: status })
          .then(() => {
            Swal.fire("Updated!", "Status updated.", "success");
            setResetDate((p) => !p);
          })
          .catch((err) => {
            Swal.fire("Error", err.message || "Update failed", "error");
          });
      }
    });
  };

  // react-select options
  const astroOptions = astrologers.map((a) => ({
    value: a._id,
    label: a.name,
    phone: a.phone_number || null,
  }));

  const customOption = ({ innerProps, label, data }) => (
    <div
      {...innerProps}
      className="d-flex justify-content-between align-items-center px-3 py-2"
    >
      <span>{label}</span>
      {data?.phone_number && (
        <small className="text-muted">({data?.phone_number})</small>
      )}
    </div>
  );

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label mb-0">Puja Bookings</h6>
              <div className="d-flex align-items-center gap-2">
                <div className="form-group mb-0">
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search bookings..."
                    value={globalsearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                  />
                </div>
                <CustomRangepicker
                  GetDateRange={(e) => prepareSearch("date_range", e)}
                  resetdate={resetdate}
                />
                <button
                  className="btn btn-outline-warning"
                  onClick={resetFilter}
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
              {tabs.map((t) => (
                <li key={t.key} className="nav-item">
                  <a
                    className={`nav-link ${tab === t.key ? "active" : ""}`}
                    onClick={() => {
                      setTab(t.key);
                      setPage(1);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>

            <div className="table-responsive">
              <table className="table table-bordered text-nowrap w-100">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>User</th>
                    <th>Puja Name</th>
                    <th>Occasion</th>
                    <th>Date & Time</th>
                    <th>Astrologer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th className="action_head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan="10" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : list.length > 0 ? (
                    list.map((row, i) => (
                      <tr key={row._id}>
                        <td>{(page - 1) * itemperpage + i + 1}</td>
                        <td>{row.user_id?.name || "N/A"}</td>
                        <td>{row.puja_name}</td>
                        <td>{row.occasion || "-"}</td>
                        <td>
                          {new Date(row.scheduled_date).toLocaleDateString()}{" "}
                          <br />
                          {row.schedule_time}
                        </td>
                        <td>{row.astrologer_id?.name || "-"}</td>
                        <td>₹{row.amount}</td>
                        <td>
                          <span
                            className={`badge badge-${
                              row.booking_status === "Completed"
                                ? "success"
                                : row.booking_status === "Cancelled"
                                  ? "danger"
                                  : "warning"
                            }`}
                          >
                            {row.booking_status}
                          </span>
                        </td>
                        <td>
                          {row.payment_method} ({row.payment_status})
                        </td>
                        <td>
                          {row.booking_status === "Requested" && (
                            <button
                              className="btn btn-sm btn-info me-2"
                              onClick={() => openAssignModal(row._id)}
                            >
                              Assign
                            </button>
                          )}
                          {/* {row.booking_status === "Confirmed" && (
                            <button
                              className="btn btn-sm btn-primary me-2"
                              onClick={() => updateStatus(row._id, "In Progress")}
                            >
                              Start
                            </button>
                          )} */}
                          {/* {row.booking_status === "In Progress" && (
                            <button
                              className="btn btn-sm btn-success me-2"
                              onClick={() => updateStatus(row._id, "Completed")}
                            >
                              Complete
                            </button>
                          )} */}
                          {["Requested", "Confirmed"].includes(
                            row.booking_status,
                          ) && (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => updateStatus(row._id, "Cancelled")}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-between align-items-center">
              <b>Total Records: {datalength}</b>
              {datalength > 0 && (
                <CustomPagination
                  datalength={datalength}
                  itemperpage={itemperpage}
                  currentPage={page}
                  setPage={setPage}
                  pageRoute={[
                    { name: "Puja Bookings", path: "/admin/puja-booking/list" },
                  ]}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Professional Assign Astrologer Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="md"
        style={{ visibility: "visible", pointerEvents: "initial" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Astrologer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              Search & Select Astrologer
            </Form.Label>
            <Select
              options={astroOptions}
              formatOptionLabel={customOption}
              onChange={setSelectedAstroOption}
              value={selectedAstroOption}
              placeholder="Type to search astrologer..."
              isLoading={astroLoader}
              isClearable
              isSearchable
              noOptionsMessage={() =>
                astroSearch ? "No astrologers found" : "Type to search..."
              }
              loadingMessage={() => "Loading astrologers..."}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  minHeight: 48,
                  borderRadius: 8,
                }),
              }}
            />
          </Form.Group>

          {selectedAstroOption && (
            <div className="alert alert-info py-2 mt-3">
              <strong>Selected:</strong> {selectedAstroOption.label}
              {selectedAstroOption.phone && ` (${selectedAstroOption.phone})`}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedAstroOption || astroLoader}
          >
            Assign Astrologer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PujaBookingsList;
